import React, { useState } from "react";
import { Alert, Label, Form } from "reactstrap";
import { useDispatch } from "react-redux";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useFormik } from "formik";
import * as Yup from "yup";
import { sendMfaCode, saveMfaMobile, verifyMfaCode } from "src/slices/Auth/Login/thunk";

// ─── Types ───────────────────────────────────────────────────────────────────

type MfaSubStep = "choice" | "enter_mobile" | "otp";

interface MfaStepProps {
    /** The short-lived encrypted token returned by POST /login */
    mfaToken: string;
    /** Called with the full login response once MFA is verified */
    onSuccess: (loginData: any) => void;
    /** Go back to the email/password form */
    onBack: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

const MfaStep: React.FC<MfaStepProps> = ({ mfaToken, onSuccess, onBack }) => {
    const dispatch = useDispatch();

    const [subStep, setSubStep]     = useState<MfaSubStep>("choice");
    const [channel, setChannel]     = useState<"sms" | "email">("email");
    const [masked, setMasked]       = useState("");
    const [error, setError]         = useState("");
    const [loading, setLoading]     = useState(false);

    // Mobile number state — PhoneInput is uncontrolled by Formik
    const [phone, setPhone]         = useState("");
    const [phoneError, setPhoneError] = useState<string | null>(null);

    // ── Channel selection ────────────────────────────────────────────────────

    const handleChooseChannel = async (selected: "sms" | "email") => {
        setChannel(selected);
        setError("");
        setLoading(true);
        try {
            const result = await dispatch(sendMfaCode(mfaToken, selected));
            if (result?.requires_mobile) {
                setSubStep("enter_mobile");
            } else if (result?.masked) {
                setMasked(result.masked);
                setSubStep("otp");
            } else {
                setError(result?.message || "Failed to send OTP. Please try again.");
            }
        } catch (e: any) {
            setError(e?.message || "Failed to send OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ── Mobile entry ─────────────────────────────────────────────────────────

    const handleMobileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPhoneError(null);
        setError("");

        if (!phone) {
            setPhoneError("Mobile number is required.");
            return;
        }

        // Validate format using libphonenumber-js (same approach as AddEditUser)
        const parsed = parsePhoneNumberFromString('+' + phone);
        if (!parsed?.isValid()) {
            setPhoneError("Please enter a valid phone number for the selected country.");
            return;
        }

        setLoading(true);
        try {
            // Send digits-only — backend normalises to E.164
            const result = await dispatch(saveMfaMobile(mfaToken, phone));

            if (result?.duplicate) {
                setPhoneError("This mobile number is already associated with another user.");
            } else if (result?.error) {
                setPhoneError(result.message || "Please enter a valid phone number.");
            } else if (result?.masked) {
                setMasked(result.masked);
                setSubStep("otp");
            } else {
                setError(result?.message || "Could not save mobile number. Please try again.");
            }
        } catch (e: any) {
            setError(e?.message || "Could not save mobile number. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ── OTP entry ────────────────────────────────────────────────────────────

    const otpForm = useFormik({
        initialValues: { code: "" },
        validationSchema: Yup.object({
            code: Yup.string()
                .required("Please enter the verification code")
                .matches(/^\d{6}$/, "Code must be exactly 6 digits"),
        }),
        onSubmit: async (values) => {
            setError("");
            setLoading(true);
            try {
                const result = await dispatch(verifyMfaCode(mfaToken, values.code, channel));
                if (result?.access_token) {
                    onSuccess(result);
                } else {
                    setError(result?.message || "Invalid code. Please try again.");
                    otpForm.resetForm();
                }
            } catch (e: any) {
                setError(e?.message || "Invalid or expired code. Please try again.");
                otpForm.resetForm();
            } finally {
                setLoading(false);
            }
        },
    });

    const handleResend = async () => {
        setError("");
        setLoading(true);
        otpForm.resetForm();
        try {
            const result = await dispatch(sendMfaCode(mfaToken, channel));
            if (result?.masked) setMasked(result.masked);
        } catch (e: any) {
            setError(e?.message || "Failed to resend code. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    // ── Step 1: choose channel ───────────────────────────────────────────────
    if (subStep === "choice") {
        return (
            <div className="text-center">
                <div className="mb-4">
                    <i className="uil uil-shield-check text-info" style={{ fontSize: 40 }}></i>
                    <h5 className="mt-2">Two-Factor Authentication</h5>
                    <p className="text-muted mb-0">
                        How would you like to receive your verification code?
                    </p>
                </div>

                {error && <Alert className="alert-danger mb-3">{error}</Alert>}

                <div className="d-grid gap-3 mb-4">
                    <button
                        className="btn btn-outline-info w-100 py-3"
                        type="button"
                        onClick={() => handleChooseChannel("sms")}
                        disabled={loading}
                    >
                        <div className="d-flex align-items-center justify-content-center gap-2">
                            <i className="uil uil-mobile-android fs-4"></i>
                            <div className="text-start">
                                <div className="fw-semibold">SMS</div>
                                <small className="text-muted">Send a code to your mobile phone</small>
                            </div>
                        </div>
                    </button>

                    <button
                        className="btn btn-outline-info w-100 py-3"
                        type="button"
                        onClick={() => handleChooseChannel("email")}
                        disabled={loading}
                    >
                        <div className="d-flex align-items-center justify-content-center gap-2">
                            <i className="uil uil-envelope fs-4"></i>
                            <div className="text-start">
                                <div className="fw-semibold">Email</div>
                                <small className="text-muted">Send a code to your email address</small>
                            </div>
                        </div>
                    </button>
                </div>

                {loading && (
                    <div className="mb-3 text-muted small">
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Sending…
                    </div>
                )}

                <button className="btn btn-link text-muted p-0 small" onClick={onBack}>
                    ← Back to sign in
                </button>
            </div>
        );
    }

    // ── Step 2: collect mobile number ────────────────────────────────────────
    if (subStep === "enter_mobile") {
        return (
            <div className="text-center">
                <div className="mb-4">
                    <i className="uil uil-mobile-android text-info" style={{ fontSize: 40 }}></i>
                    <h5 className="mt-2">Enter Your Mobile Number</h5>
                    <p className="text-muted mb-0">
                        We need your phone number to send the verification code.
                    </p>
                </div>

                {error && <Alert className="alert-danger mb-3">{error}</Alert>}

                <Form onSubmit={handleMobileSubmit} className="text-start">
                    <div className="mb-3">
                        <Label>Mobile Number</Label>
                        <PhoneInput
                            country="us"
                            value={phone}
                            onChange={(value) => {
                                setPhone(value);
                                setPhoneError(null);
                            }}
                            inputStyle={{ width: '100%', height: '38px' }}
                            disabled={loading}
                        />
                        {phoneError && (
                            <div style={{ color: 'red', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                                {phoneError}
                            </div>
                        )}
                    </div>

                    <button className="btn btn-info w-100" type="submit" disabled={loading}>
                        {loading
                            ? <span className="spinner-border spinner-border-sm"></span>
                            : "Save & Send Code"}
                    </button>
                </Form>

                <div className="mt-3">
                    <button
                        className="btn btn-link text-muted p-0 small"
                        type="button"
                        onClick={() => { setError(""); setPhoneError(null); setPhone(""); setSubStep("choice"); }}
                    >
                        ← Choose a different method
                    </button>
                </div>
            </div>
        );
    }

    // ── Step 3: enter OTP ────────────────────────────────────────────────────
    return (
        <div className="text-center">
            <div className="mb-4">
                <i className="uil uil-envelope-shield text-info" style={{ fontSize: 40 }}></i>
                <h5 className="mt-2">Enter Verification Code</h5>
                <p className="text-muted mb-0">
                    We sent a 6-digit code to <strong>{masked}</strong>
                </p>
            </div>

            {error && <Alert className="alert-danger mb-3">{error}</Alert>}

            <Form onSubmit={otpForm.handleSubmit} className="text-start">
                <div className="form-floating form-floating-custom mb-1">
                    <input
                        type="text"
                        name="code"
                        className="form-control"
                        id="input-otp"
                        placeholder="123456"
                        maxLength={6}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        onChange={otpForm.handleChange}
                        onBlur={otpForm.handleBlur}
                        value={otpForm.values.code}
                    />
                    <Label htmlFor="input-otp">Verification Code</Label>
                    <div className="form-floating-icon">
                        <i className="uil uil-lock-alt"></i>
                    </div>
                </div>
                {otpForm.touched.code && otpForm.errors.code && (
                    <div className="text-danger small mb-3">{otpForm.errors.code}</div>
                )}

                <button className="btn btn-info w-100" type="submit" disabled={loading}>
                    {loading
                        ? <span className="spinner-border spinner-border-sm"></span>
                        : "Verify"}
                </button>
            </Form>

            <div className="mt-3 d-flex justify-content-between align-items-center">
                <button
                    className="btn btn-link text-muted p-0 small"
                    type="button"
                    onClick={() => { setError(""); otpForm.resetForm(); setSubStep("choice"); }}
                >
                    ← Different method
                </button>
                <button
                    className="btn btn-link text-info p-0 small"
                    type="button"
                    onClick={handleResend}
                    disabled={loading}
                >
                    Resend code
                </button>
            </div>
        </div>
    );
};

export default MfaStep;
